import { useQuery } from '@tanstack/react-query'
import { type ColumnFiltersState } from '@tanstack/react-table'

import { fetchWithHeaders } from 'utils'

import { GrapeAPIError, type GrapeAPIErrorType } from './GrapeAPIError'
import { type PaginatedResponse } from './types'
import { type Employer } from '../brokers/types'

export type GetBrokersEmployersParams = {
  pagination: {
    pageIndex: number
    pageSize: number
  }
  filters: ColumnFiltersState
}

export function useGetBrokersEmployersQueryKey ({
  pagination,
  filters,
}: GetBrokersEmployersParams) {
  return ['brokers', 'employers', 'pagination', pagination, 'filters', filters]
}

export async function fetchBrokersEmployers ({
  pagination,
  filters,
}: GetBrokersEmployersParams): Promise<PaginatedResponse<Employer>> {
  const searchParams = new URLSearchParams()

  searchParams.set('page[number]', pagination.pageIndex.toString())
  searchParams.set('page[size]', pagination.pageSize.toString())

  filters.forEach((filter) => {
    // Handle array filters (like review_statuses)
    if (Array.isArray(filter.value)) {
      filter.value.forEach((val) => {
        searchParams.append(`filter[${filter.id}][]`, val)
      })
    } else {
      searchParams.append(`filter[${filter.id}]`, filter.value as string)
    }
  })

  const response = await fetchWithHeaders(`/api/web/brokers/employers?${searchParams.toString()}`)

  if (!response.ok) {
    const errorJson = await response.json()
    throw GrapeAPIError({
      status: response.status,
      message: errorJson.message,
      errors: errorJson.errors,
    })
  }

  return response.json()
}

export function useGetBrokersEmployers ({
  pagination,
  filters,
}: GetBrokersEmployersParams) {
  return useQuery<PaginatedResponse<Employer>, GrapeAPIErrorType>({
    queryKey: useGetBrokersEmployersQueryKey({ pagination, filters }),
    queryFn: () => fetchBrokersEmployers({ pagination, filters }),
  })
}
